/**
 * WASM Service
 *
 * Provides lazy-loading and caching of the Snowhub WASM module.
 * Falls back to JavaScript implementation if WASM is unavailable.
 */

import pako from 'pako';

// Module state
let wasmModule = null;
let wasmLoadPromise = null;
let wasmAvailable = null;

/**
 * Check if WASM is supported in the current environment
 */
export function isWasmSupported() {
    try {
        if (typeof WebAssembly === 'object' &&
            typeof WebAssembly.instantiate === 'function') {
            // Test with a minimal valid WASM module
            const module = new WebAssembly.Module(
                Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00)
            );
            return module instanceof WebAssembly.Module;
        }
    } catch (e) {
        console.warn('[WASM] WebAssembly not supported:', e);
    }
    return false;
}

/**
 * Load the WASM module (lazy, cached)
 *
 * @returns {Promise<Object|null>} The WASM module or null if unavailable
 */
export async function loadWasmModule() {
    // Return cached module if already loaded
    if (wasmModule) {
        return wasmModule;
    }

    // Return existing load promise if loading in progress
    if (wasmLoadPromise) {
        return wasmLoadPromise;
    }

    // Check if WASM is supported
    if (!isWasmSupported()) {
        console.warn('[WASM] WebAssembly not supported, using JavaScript fallback');
        wasmAvailable = false;
        return null;
    }

    // Start loading
    wasmLoadPromise = (async () => {
        try {
            // Dynamic import of the WASM package
            // The package should be built with wasm-pack and placed in src/wasm/pkg/
            const wasm = await import('../wasm/pkg/snowhub_wasm.js');

            // Initialize the WASM module (calls the start function)
            await wasm.default();

            // Verify the module loaded correctly
            if (wasm.healthCheck && wasm.healthCheck()) {
                console.log(`[WASM] Module loaded successfully (v${wasm.getVersion()})`);
                wasmModule = wasm;
                wasmAvailable = true;
                return wasm;
            } else {
                throw new Error('Health check failed');
            }
        } catch (error) {
            console.warn('[WASM] Failed to load module, using JavaScript fallback:', error);
            wasmAvailable = false;
            return null;
        } finally {
            wasmLoadPromise = null;
        }
    })();

    return wasmLoadPromise;
}

/**
 * Check if WASM is available and loaded
 */
export function isWasmAvailable() {
    return wasmAvailable === true;
}

/**
 * Decompress and parse using pure JavaScript (for benchmarking)
 * @param {ArrayBuffer|Uint8Array} compressedData
 * @returns {Promise<{data: any, metrics: Object}>}
 */
export async function decompressAndParseJS(compressedData) {
    const metrics = {
        method: 'javascript',
        compressedSize: compressedData.byteLength || compressedData.length,
        decompressedSize: 0,
        decompressTimeMs: 0,
        parseTimeMs: 0,
        totalTimeMs: 0,
    };

    const startTotal = performance.now();

    // Ensure we have a Uint8Array
    let compressedArray;
    if (compressedData instanceof Uint8Array) {
        compressedArray = compressedData;
    } else if (compressedData instanceof ArrayBuffer) {
        compressedArray = new Uint8Array(compressedData);
    } else {
        throw new Error('Unsupported compressed data format');
    }

    // Decompress
    const startDecompress = performance.now();
    const decompressedArray = pako.inflate(compressedArray);
    metrics.decompressTimeMs = performance.now() - startDecompress;
    metrics.decompressedSize = decompressedArray.length;

    // Parse JSON
    const startParse = performance.now();
    const decompressedText = new TextDecoder().decode(decompressedArray);
    const data = JSON.parse(decompressedText);
    metrics.parseTimeMs = performance.now() - startParse;

    metrics.totalTimeMs = performance.now() - startTotal;

    logPerformance(metrics);

    return { data, metrics };
}

/**
 * Decompress and parse gzip-compressed JSON data
 *
 * Uses WASM if available, falls back to JavaScript (pako + JSON.parse)
 *
 * @param {ArrayBuffer|Uint8Array} compressedData - Gzip compressed data
 * @param {Object} options - Options object
 * @param {Function} options.jsFallback - JavaScript fallback function (optional, uses built-in if not provided)
 * @returns {Promise<{data: any, metrics: Object}>} Parsed data and performance metrics
 */
export async function decompressAndParse(compressedData, { jsFallback } = {}) {
    const metrics = {
        method: 'unknown',
        compressedSize: compressedData.byteLength || compressedData.length,
        decompressedSize: 0,
        decompressTimeMs: 0,
        parseTimeMs: 0,
        totalTimeMs: 0,
    };

    const startTotal = performance.now();

    // Try WASM first
    const wasm = await loadWasmModule();

    if (wasm) {
        try {
            // Ensure we have a Uint8Array
            const dataArray = compressedData instanceof Uint8Array
                ? compressedData
                : new Uint8Array(compressedData);

            const result = wasm.decompressAndParse(dataArray);

            if (result.success) {
                metrics.method = 'wasm';
                metrics.decompressedSize = result.decompressedSize;
                metrics.decompressTimeMs = result.decompressTimeMs;
                metrics.parseTimeMs = result.parseTimeMs;
                metrics.totalTimeMs = performance.now() - startTotal;

                // Debug: Check if data has expected structure
                const data = result.data;
                if (data && typeof data === 'object') {
                    console.log('[WASM] Parsed data keys:', Object.keys(data));
                    if (!data.week || !data.rep) {
                        console.warn('[WASM] Missing expected keys. Has week:', !!data.week, 'Has rep:', !!data.rep);
                        // Try to find the data - maybe it's nested differently
                        console.log('[WASM] Data structure sample:', JSON.stringify(data).substring(0, 500));
                    }
                }

                logPerformance(metrics);

                return {
                    data: result.data,
                    metrics,
                };
            } else {
                console.warn('[WASM] Parse failed, falling back to JS:', result.errorMessage);
            }
        } catch (error) {
            console.warn('[WASM] Error during parse, falling back to JS:', error);
        }
    }

    // JavaScript fallback
    const fallbackFn = jsFallback || (async (data) => {
        const result = await decompressAndParseJS(data);
        return result.data;
    });

    metrics.method = 'javascript';
    const jsStart = performance.now();

    try {
        // Use the built-in JS parser for detailed metrics
        const jsResult = await decompressAndParseJS(compressedData);

        // Merge metrics
        metrics.decompressedSize = jsResult.metrics.decompressedSize;
        metrics.decompressTimeMs = jsResult.metrics.decompressTimeMs;
        metrics.parseTimeMs = jsResult.metrics.parseTimeMs;
        metrics.totalTimeMs = performance.now() - startTotal;

        // Log is already done in decompressAndParseJS, so don't double-log

        return {
            data: jsResult.data,
            metrics,
        };
    } catch (error) {
        metrics.totalTimeMs = performance.now() - startTotal;
        throw error;
    }
}

/**
 * Log performance metrics to console
 */
function logPerformance(metrics) {
    const compressionRatio = metrics.decompressedSize > 0
        ? (metrics.compressedSize / metrics.decompressedSize * 100).toFixed(1)
        : 'N/A';

    const label = metrics.method === 'wasm' ? '[WASM]' : '[JS]';

    console.log(
        `${label} Data processed via ${metrics.method}: ` +
        `${(metrics.compressedSize / 1024 / 1024).toFixed(2)}MB compressed -> ` +
        `${(metrics.decompressedSize / 1024 / 1024).toFixed(2)}MB (${compressionRatio}% ratio) ` +
        `in ${metrics.totalTimeMs.toFixed(0)}ms ` +
        `(decompress: ${metrics.decompressTimeMs.toFixed(0)}ms, parse: ${metrics.parseTimeMs.toFixed(0)}ms)`
    );
}

/**
 * Preload the WASM module in the background
 *
 * Call this early in the app lifecycle to ensure WASM is ready when needed.
 */
export function preloadWasm() {
    // Don't await - let it load in background
    loadWasmModule().catch(err => {
        console.warn('[WASM] Preload failed:', err);
    });
}
